import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sidebar } from './Dashboard';
import { API } from '../App';
import { describeBillingError, isBillingError } from '../billingMessages';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  addButton: {
    padding: '12px 24px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  label: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    marginBottom: '6px',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  saveButton: {
    padding: '12px 24px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginRight: '12px',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #0f3460',
  },
  td: {
    color: '#ffffff',
    fontSize: '14px',
    padding: '14px 16px',
    borderBottom: '1px solid #0f3460',
  },
  badge: {
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  badgeAdmin: {
    backgroundColor: '#3a0a2a',
    color: '#e94560',
  },
  badgeObserver: {
    backgroundColor: '#0a2a3a',
    color: '#4a9eff',
  },
  badgeManager: {
    backgroundColor: '#0a3a2a',
    color: '#00cc88',
  },
  badgeActive: {
    backgroundColor: '#0a3a0a',
    color: '#00cc00',
  },
  badgeInactive: {
    backgroundColor: '#3a0a0a',
    color: '#e94560',
  },
  actionButton: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: '1px solid #0f3460',
    borderRadius: '6px',
    color: '#a0a0b0',
    fontSize: '12px',
    cursor: 'pointer',
    marginRight: '8px',
  },
  success: {
    color: '#00cc00',
    fontSize: '14px',
    marginBottom: '16px',
  },
  error: {
    color: '#e94560',
    fontSize: '14px',
    marginBottom: '16px',
  },
  hint: {
    color: '#a0a0b0',
    fontSize: '12px',
    marginTop: '4px',
  },
};

function Users() {
  const token = localStorage.getItem('admin_token');
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'observer',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadUsers() {
    const res = await fetch(`${API}/admin/users`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) setUsers(data);
  }

  async function addUser() {
    if (!newUser.first_name || !newUser.last_name || !newUser.email || !newUser.password) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');
    setShowUpgrade(false);
    try {
      const res = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newUser),
      });
      if (res.ok) {
        setMessage('User added successfully');
        setNewUser({ first_name: '', last_name: '', email: '', password: '', role: 'observer' });
        setShowAdd(false);
        loadUsers();
      } else {
        const data = await res.json();
        setError(describeBillingError(data.detail, 'Failed to add user'));
        setShowUpgrade(isBillingError(data.detail));
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setLoading(false);
  }

  async function toggleUser(id, active) {
    await fetch(`${API}/admin/users/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ active: !active }),
    });
    loadUsers();
  }

async function resetPassword(userId) {
    const newPassword = prompt('Enter new password for this user (minimum 8 characters):');
    if (!newPassword) return;
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ new_password: newPassword }),
      });
      if (res.ok) {
        setMessage('Password reset successfully');
      } else {
        setError('Failed to reset password');
      }
    } catch (e) {
      setError('Could not connect to server');
    }
  }

  return (
    <div style={styles.container}>
      <Sidebar active="Users" />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Users</div>
        <div style={styles.pageSubtitle}>Manage observers and administrators</div>

        {message && <div style={styles.success}>{message}</div>}
        {error && (
          <div style={styles.error}>
            {error}
            {showUpgrade && (
              <Link to="/billing" style={{ marginLeft: '12px', color: '#e94560', fontWeight: 'bold' }}>
                Go to Billing →
              </Link>
            )}
          </div>
        )}

        <div style={styles.topBar}>
          <div />
          <button style={styles.addButton} onClick={() => setShowAdd(!showAdd)}>
            + Add User
          </button>
        </div>

        {showAdd && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Add New User</div>
            <div style={styles.grid}>
              <div>
                <label style={styles.label}>First Name</label>
                <input
                  style={styles.input}
                  value={newUser.first_name}
                  onChange={e => setNewUser({ ...newUser, first_name: e.target.value })}
                  placeholder="First name"
                />
              </div>
              <div>
                <label style={styles.label}>Last Name</label>
                <input
                  style={styles.input}
                  value={newUser.last_name}
                  onChange={e => setNewUser({ ...newUser, last_name: e.target.value })}
                  placeholder="Last name"
                />
              </div>
              <div>
                <label style={styles.label}>Email</label>
                <input
                  style={styles.input}
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@casino.com"
                />
              </div>
              <div>
                <label style={styles.label}>Password</label>
                <input
                  style={styles.input}
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Set initial password"
                />
                <div style={styles.hint}>User can change this after first login</div>
              </div>
              <div>
                <label style={styles.label}>Role</label>
                <select
                  style={styles.select}
                  value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="observer">Observer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <button style={styles.saveButton} onClick={addUser} disabled={loading}>
              {loading ? 'Saving...' : 'Save User'}
            </button>
            <button style={styles.cancelButton} onClick={() => setShowAdd(false)}>
              Cancel
            </button>
          </div>
        )}

        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td style={styles.td}>{user.first_name} {user.last_name}</td>
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      ...(user.role === 'admin'
                        ? styles.badgeAdmin
                        : user.role === 'manager'
                          ? styles.badgeManager
                          : styles.badgeObserver)
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      ...(user.active ? styles.badgeActive : styles.badgeInactive)
                    }}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button
                      style={styles.actionButton}
                      onClick={() => toggleUser(user.id, user.active)}
                    >
                      {user.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      style={styles.actionButton}
                      onClick={() => resetPassword(user.id)}
                    >
                      Reset Password
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td style={{ ...styles.td, color: '#a0a0b0' }} colSpan="5">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Users;